import { getAuth } from 'firebase/auth'
import { debug } from '@helpers/debug'
import { collectionGroup, query, where, orderBy, limit, getFirestore, getDocs } from 'firebase/firestore'
import { logger } from '@helpers/logger'
import { getUserTournamentEntry } from './userTournamentEntryService'

/**
 * Retrieves up to 100 entrant documents where `uid` matches the current user's ID and `eventsRemaining` is greater than or equal to 0,
 * ordered by `eventsRemaining` in ascending order.
 * @returns {Promise<Array>} - A promise that resolves to an array of tournament pool documents.
 */
export const getCompletedTournamentPools = async (setCompletedTournamentPoolData, setHasCompletedTournaments) => {
  const performanceLogs = []
  const isLoggingEnabled = debug.performanceLoggingEnabled === true
  const params = {}
  const logComplete = logger.serviceRequest('getCompetedTournamentPools', params)

  try {
    const auth = getAuth()
    const currentUser = auth.currentUser

    if (!currentUser) {
      throw new Error('No authenticated user found.')
    }

    const userKey = currentUser.uid
    const db = getFirestore()

    const entrantsQuery = query(
      collectionGroup(db, 'entrants'),
      where('uid', '==', userKey),
      where('eventsRemaining', '<=', 0),
      orderBy('eventsRemaining', 'desc'),
      orderBy('createdUtcTimeStamp', 'desc'),
      limit(100),
    )

    const entrantsSnapshot = await getDocs(entrantsQuery)
    if (entrantsSnapshot.empty) {
      setHasCompletedTournaments(false)
      setCompletedTournamentPoolData([])
      return // Exit early if no tournaments
    } else {
      setHasCompletedTournaments(true)
      const entries = await Promise.all(
        entrantsSnapshot.docs.map(async (entrantDoc) => {
          const tournamentKey = entrantDoc.ref.parent.parent.id
          const userTournamentEntry = await getUserTournamentEntry(tournamentKey)
          if (userTournamentEntry) {
            userTournamentEntry.joined = true
            return userTournamentEntry
          }
          return null
        }),
      )
      setCompletedTournamentPoolData(entries.filter((entry) => entry !== null))
      logComplete()
    }
  } catch (error) {
    logger.error('getCompetedTournamentPools', error)
    if (isLoggingEnabled) {
      logger.log('Performance Logs:', performanceLogs)
    }
    setHasCompletedTournaments(false)
    setCompletedTournamentPoolData([])
    logComplete()
  }
}
